import { BottomTabBarProps } from '@react-navigation/bottom-tabs';
import * as Haptics from 'expo-haptics';
import { useEffect } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { IconSymbol } from '@/components/ui/icon-symbol';
import { Colors } from '@/constants/theme';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';

interface TabConfig {
  name: string;
  icon: string;
  label: string;
}

const TAB_CONFIGS: TabConfig[] = [
  { name: 'index', icon: 'house.fill', label: 'home' },
  { name: 'todo', icon: 'checklist', label: 'to do' },
  { name: 'calendar', icon: 'calendar', label: 'calendar' },
  { name: 'settings', icon: 'person.fill', label: 'me' },
];

// Tabs to render on the left side of the center FAB spacer
const LEFT_TABS = ['index', 'todo'];
// Tabs to render on the right side of the center FAB spacer
const RIGHT_TABS = ['calendar', 'settings'];

// Animation and sizing constants
export const TAB_BAR_HEIGHT = 65;
const ICON_SIZE = 28;
const ACTIVE_ICON_SIZE = 28;
const TAB_SPACING = 4;
const ACTIVE_TRANSLATION_Y = -6;
const ANIMATION_DURATION = 100;
const UNDERLINE_WIDTH = 28;
const UNDERLINE_HEIGHT = 3;
const UNDERLINE_BORDER_RADIUS = 1.5;

interface TabButtonProps {
  route: any;
  index: number;
  isFocused: boolean;
  onPress: () => void;
  tabConfig: TabConfig;
  accentColor: string;
  iconColor: string;
}

/**
 * TabButton Component
 * Individual tab button with animated pop-up effect and underline indicator.
 * Uses react-native-reanimated for smooth UI thread animations.
 */
function TabButton({
  route,
  index,
  isFocused,
  onPress,
  tabConfig,
  accentColor,
  iconColor,
}: TabButtonProps) {
  // Shared values for animations
  const translateY = useSharedValue(0);
  const iconScale = useSharedValue(1);
  const underlineWidth = useSharedValue(0);

  // Animate when focus changes
  useEffect(() => {
    if (isFocused) {
      // Slide up animation (no bounce)
      translateY.value = withTiming(ACTIVE_TRANSLATION_Y, { duration: ANIMATION_DURATION });

      // Scale icon slightly
      iconScale.value = withTiming(1.08, { duration: ANIMATION_DURATION });

      // Show underline
      underlineWidth.value = withTiming(UNDERLINE_WIDTH, { duration: ANIMATION_DURATION });
    } else {
      // Return to normal position
      translateY.value = withTiming(0, { duration: ANIMATION_DURATION });

      iconScale.value = withTiming(1, { duration: ANIMATION_DURATION });
      underlineWidth.value = withTiming(0, { duration: ANIMATION_DURATION });
    }
  }, [isFocused, iconScale, translateY, underlineWidth]);

  // Animated styles
  const animatedContainerStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }));

  const animatedIconStyle = useAnimatedStyle(() => ({
    transform: [{ scale: iconScale.value }],
  }));

  const animatedUnderlineStyle = useAnimatedStyle(() => ({
    width: underlineWidth.value,
    opacity: underlineWidth.value / UNDERLINE_WIDTH, // Fade in/out
  }));

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityState={isFocused ? { selected: true } : {}}
      accessibilityLabel={tabConfig.label}
      testID={`tab-${route.name}`}
      style={styles.tabButton}
    >
      {({ pressed }) => (
        <Animated.View style={[styles.tabButtonContent, animatedContainerStyle]}>
          {/* Animated Icon Container */}
          <Animated.View style={[styles.iconContainer, animatedIconStyle]}>
            <IconSymbol
              name={tabConfig.icon as any}
              size={isFocused ? ACTIVE_ICON_SIZE : ICON_SIZE}
              color={isFocused ? accentColor : iconColor}
            />
          </Animated.View>

          {/* Label */}
          <Text
            style={[
              styles.tabLabel,
              {
                color: isFocused ? accentColor : iconColor,
                opacity: pressed ? 0.6 : 1,
                fontFamily: getBodyFont(),
              },
            ]}
          >
            {tabConfig.label}
          </Text>

          {/* Underline Indicator */}
          <Animated.View
            style={[
              styles.underlineIndicator,
              { backgroundColor: accentColor },
              animatedUnderlineStyle,
            ]}
          />
        </Animated.View>
      )}
    </Pressable>
  );
}

/**
 * Custom Tab Bar Component
 * Renders a horizontal bottom navigation bar with animated icon buttons.
 * All 5 tabs are evenly spaced across the full width.
 * Features pop-up animation and underline indicator for active state.
 * Follows React Navigation's BottomTabBarProps interface.
 */
export function CustomTabBar({ state, descriptors, navigation }: BottomTabBarProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const accentColor = palette.primary;
  const iconColor = palette.tabIconDefault;
  const insets = useSafeAreaInsets();

  const handlePress = (route: any, isFocused: boolean) => {
    if (process.env.EXPO_OS === 'ios') {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    }

    const event = navigation.emit({
      type: 'tabPress',
      target: route.key,
      canPreventDefault: true,
    });

    if (!isFocused && !event.defaultPrevented) {
      navigation.navigate(route.name, route.params);
    }
  };

  const renderTab = (route: any, index: number) => {
    const isFocused = state.index === index;
    const tabConfig = TAB_CONFIGS.find(config => config.name === route.name);

    if (!tabConfig) return null;

    return (
      <TabButton
        key={route.key}
        route={route}
        index={index}
        isFocused={isFocused}
        onPress={() => handlePress(route, isFocused)}
        tabConfig={tabConfig}
        accentColor={accentColor}
        iconColor={iconColor}
      />
    );
  };

  const visibleRoutes = state.routes.filter(route =>
    TAB_CONFIGS.some(c => c.name === route.name),
  );
  const leftRoutes = visibleRoutes.filter(r => LEFT_TABS.includes(r.name));
  const rightRoutes = visibleRoutes.filter(r => RIGHT_TABS.includes(r.name));

  return (
    <View style={styles.tabBarWrapper}>
      {/* Tab bar with transparent background */}
      <View
        style={[
          styles.tabBar,
          {
            paddingBottom: Math.max(insets.bottom, 12),
            backgroundColor: 'transparent',
          },
        ]}
      >
        {/* Left tabs */}
        {leftRoutes.map((route) => {
          const index = state.routes.indexOf(route);
          return (
            <View key={route.key} style={styles.tabPosition}>
              {renderTab(route, index)}
            </View>
          );
        })}

        {/* Center spacer for FAB */}
        <View style={styles.tabPosition} />

        {/* Right tabs */}
        {rightRoutes.map((route) => {
          const index = state.routes.indexOf(route);
          return (
            <View key={route.key} style={styles.tabPosition}>
              {renderTab(route, index)}
            </View>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  tabBarWrapper: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 20,
  },
  tabBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingTop: 8,
    paddingHorizontal: 16,
    zIndex: 2,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
  },
  tabPosition: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-end',
    height: '100%',
    overflow: 'visible',
  },
  tabButton: {
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingVertical: 8,
    minWidth: 48,
    minHeight: 48,
  },
  tabButtonContent: {
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  iconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: TAB_SPACING,
  },
  tabLabel: {
    fontSize: 10,
    fontWeight: '500',
    textAlign: 'center',
    marginBottom: 2,
  },
  underlineIndicator: {
    position: 'absolute',
    bottom: -4,
    height: UNDERLINE_HEIGHT,
    borderRadius: UNDERLINE_BORDER_RADIUS,
  },
});